import React from "react";
import { useSearchParams } from "react-router-dom";
import { PageKey, useAuth } from "../../common/auth";
import { getSellerApplications } from "../../common/requests/sellers/endpoints";
import { EmptyState, PageShell, useResource } from "../../ui";
import UserManagement from "../UserManagement";
import SellerApplications from "../SellerApplications";
import { pendingCount } from "../SellerApplications/applicationData";
import NotificationsBroadcast from "../NotificationsBroadcast";

type TabKey = "users" | "applications" | "messages";

const TABS: { key: TabKey; label: string; permission: PageKey; description: string }[] = [
  {
    key: "users",
    label: "Users",
    permission: "registered_users",
    description: "Everyone on the marketplace: seller status, fees and dashboard access.",
  },
  {
    key: "applications",
    label: "Seller applications",
    permission: "seller_creation",
    description: "Buyers who applied to sell from their account. Approving makes them a seller and emails them.",
  },
  {
    key: "messages",
    label: "Messages",
    permission: "notifications",
    description: "Send a Telegram message from the Inflos bot to selected users.",
  },
];

/** People in one place: the user list, seller applications and Telegram
 * messages. Each tab needs its own permission; `?tab=` picks one. */
export default function UsersAndSellers() {
  const { idToken, hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleTabs = TABS.filter((t) => hasPermission(t.permission));
  const requested = searchParams.get("tab") as TabKey | null;
  const active = visibleTabs.find((t) => t.key === requested) ?? visibleTabs[0];

  // Owned here so the tab can show the pending count; the tab reloads it after approving.
  const canSeeApplications = hasPermission("seller_creation");
  const applications = useResource(async () => (await getSellerApplications(idToken!)).data, [idToken], {
    enabled: !!idToken && canSeeApplications,
    errorText: "Couldn't load seller applications.",
  });

  if (!active) {
    return (
      <PageShell title="Users & Sellers">
        <EmptyState title="No access" body="You don't have access to any of these sections." />
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Users & Sellers"
      subtitle={active.description}
      tabs={
        visibleTabs.length > 1
          ? {
              value: active.key,
              // Switching tabs drops any open record (?user=) with the old tab.
              onChange: (key) => setSearchParams(key === visibleTabs[0].key ? {} : { tab: key }, { replace: true }),
              items: visibleTabs.map((t) => ({
                value: t.key,
                label: t.label,
                count: t.key === "applications" ? pendingCount(applications.data) || null : undefined,
              })),
            }
          : undefined
      }
    >
      {active.key === "users" && <UserManagement />}
      {active.key === "applications" && <SellerApplications applications={applications} />}
      {active.key === "messages" && <NotificationsBroadcast />}
    </PageShell>
  );
}
